import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, NgZone } from '@angular/core';
import { FormGroup, Validators, FormBuilder, AbstractControl, AsyncValidatorFn, ValidationErrors, AbstractControlOptions, ValidatorFn } from '@angular/forms';
import { Message } from 'primeng/api';
import { Observable, Subscription, map } from 'rxjs';
import { AuthService } from '../../shared/auth.service';
import { Router } from '@angular/router';

const WG = 'winning.com.au';
const WG_MESSAGE = 'Winning Group users must use Signin with Google!';
const signUpPendingMessage = {
  severity: 'info',
  summary: 'Signup Request',
  detail: 'Your request for sign up has been received. You will be emailed once your signup has been approved. Please check your inbox for notifications.'
};
declare var google: any;

@Component({
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
  googleSignInError = '';
  googleSignIn = false;
  authCodePlaceHolder = '';
  showForm: 'login' | 'lostpassword' | 'signup' | 'signupconfirm' = 'login';
  private subs: Subscription[] = [];
  loginForm: FormGroup;
  lostPasswordForm: FormGroup;
  signupForm: FormGroup;
  signupConfirmForm: FormGroup;
  changePasswordForm: FormGroup;
  email: string;
  messages: Message[];
  totpSecret = '';
  emailError = 'Please enter a valid email address';
  passwordError = '';
  @ViewChild('loginEmail') loginEmail: ElementRef;

  constructor(
    public authService: AuthService,
    private fb: FormBuilder,
    private ngZone: NgZone,
    private router: Router) { }

  ngOnInit() {
    // login form
    this.loginForm = this.fb.group({
      loginEmail: [null, { updatedOn: 'blur', validators: [Validators.required, Validators.email, this.checkEmail()] }],
      loginPassword: [null, [Validators.required]]
    });
    // lost password form
    this.lostPasswordForm = this.fb.group(
      {
        currentEmail: [null, { updatedOn: 'blur', validators: [Validators.required, Validators.email, this.checkEmail()], asyncValidators: [this.unknownEmailValidator()] }],
        newPassword: [null, [Validators.required, Validators.minLength(8)]],
        newPassword2: [null, [Validators.required]],
        authCode: [null, [Validators.required, Validators.pattern(/\d{6}/)]]
      },
      { validators: [this.passwordsMatch], updateOn: 'blur' } as AbstractControlOptions);
    // signup form
    this.signupForm = this.fb.group({
      signupEmail: [null, { updatedOn: 'focusout', validators: [Validators.required, Validators.email, this.checkEmail()] }]
    });
    // signup confirm form
    this.signupConfirmForm = this.fb.group({
      signupFullName: [null, [Validators.required, Validators.pattern(/\w+\s+\w/)]],
      newPassword: [null, [Validators.required, Validators.minLength(8)]],
      newPassword2: [null, [Validators.required]],
      signupAuthCode: [null, [Validators.required, Validators.pattern(/\d{6}/)]]
    }, { validators: [this.passwordsMatch], updateOn: 'blur' } as AbstractControlOptions);

    // if the user enters the login url and they are already logged in go straight to home page
    if (this.authService.isAuthenticated) {
      this.router.navigate(['/']);
    }

    // listen for a change in authStatus and respond appropriately
    this.subs.push(this.authService.authStatusListener.subscribe(authStatus => {
      // listen for successful login and go to home page or set errors
      this.ngZone.run(() => {
        // needs to run in ngZone if it is invoked via google signin
        if (authStatus === 'success') {
          this.router.navigate(['/']);
        } else if (authStatus === 'logged out') {
          console.log(authStatus);
        } else if (authStatus) {
          if (this.googleSignIn) {
            // this.googleSignInError = authStatus;
            this.messages = [signUpPendingMessage];
            this.showForm = 'login';
          } else {
            this.passwordError = authStatus;
            this.loginForm.get('loginPassword')?.setErrors({ invalid: true });
          }
        }
      });
    }));
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  ngAfterViewInit() {
    // initialise signin in with Google
    google.accounts.id.initialize({
      client_id: this.authService.googleClientId,
      callback: this.loginWithGoogle.bind(Response, this)
    });
    this.renderGoogleButton();
  }

  private renderGoogleButton() {
    google.accounts.id.renderButton(
      document.getElementById("googleButton"),
      {
        // theme: "filled_blue",
        // text: "continue_with"
      }  // customization attributes
    );
  }

  // this is invoked as a callback from the google.accounts.id.initialize function
  // hence we needed to pass 'this.authService' as an argument in the bind call above
  loginWithGoogle(thisComponent: any, response: any) {
    const credential = response.credential;
    thisComponent.googleSignIn = true;

    // ngZone is needed here because this is invoked by the google callback
    // and it would otherwise run outside of Angular change detection
    thisComponent.ngZone.run(() => {
      thisComponent.authService.googleRegister(credential)
        .subscribe({
          next: (response: any) => {
            thisComponent.email = response.email;
            thisComponent.authService.login(response.email, undefined, response.googleUid);
          },
          error: (error: any) => {
            thisComponent.googleSignInError = error.message;
          }
        });

    });
  }

  login() {
    if (this.loginForm.invalid) {
      return;
    }
    const email = this.loginForm.value.loginEmail;
    const domain = email.split('@')[1];
    if (domain === WG) {
      this.emailError = WG_MESSAGE;
      this.loginForm.get('loginPassword')?.setErrors({ invalid: true });
    } else {
      this.googleSignIn = false;
      this.googleSignInError = '';
      this.authService.login(this.loginForm.value.loginEmail, this.loginForm.value.loginPassword);
    }
  }

  forgotPassword() {
    this.authCodePlaceHolder = '';
    this.messages = [];
    this.passwordError = 'Passwords must be at least 8 characters in length';
    this.showForm = 'lostpassword';
  }

  showSignup() {
    this.loginForm.reset();
    this.messages = [];
    this.showForm = 'signup';
  }

  private passwordsMatch(form: FormGroup): ValidationErrors | null {
    const newPassword = form.get('newPassword');
    const newPassword2 = form.get('newPassword2');
    if (newPassword?.value !== newPassword2?.value) {
      newPassword2?.setErrors({ mismatch: true });
      return { mismatch: true };
    }
    return null;
  }

  changePassword() {
    const email = this.lostPasswordForm.get('currentEmail')?.value;
    if (!this.lostPasswordForm.invalid) {
      this.authService.changePassword(this.lostPasswordForm.value.currentEmail, this.lostPasswordForm.value.newPassword, this.lostPasswordForm.value.authCode).subscribe({
        next: (response) => {
          if (response.status === 'success') {
            this.messages = [{
              severity: 'success',
              summary: 'Password Reset',
              detail: 'Your password has been reset. Please login using your email address and new password.'
            }];
            this.clearMessages();
            this.showForm = 'login';
          }
          this.lostPasswordForm.reset();
        },
        error: (error) => {
          if (error.message.includes('email')) {
            this.lostPasswordForm.get('currentEmail')?.setErrors({ invalid: true });
          }
          if (error.message.includes('code')) {
            this.lostPasswordForm.get('authCode')?.setErrors({ invalid: true });
          }
        }
      });
    }
  }

  clearMessages(delay = 7000) {
    setTimeout(() => {
      this.messages = [];
    }, delay);
  }

  unknownEmailValidator(): AsyncValidatorFn {
    return (email: AbstractControl | null): Observable<ValidationErrors | null> => {
      return this.authService.lostPasswordEmail(email?.value)
        .pipe(
          map(resp => {
            if (resp.status === 'failure') {
              this.emailError = 'Unknown email address or signed up with Google';
              return { unknownEmail: true };
            } else {
              this.authCodePlaceHolder = `Enter the 6 digit authenticator code just sent to ${this.lostPasswordForm.value.currentEmail}`;
              return null;
            }
          })
        );
    };
  }

  checkEmail(): ValidatorFn {
    return (control: AbstractControl | null): ValidationErrors | null => {
      this.emailError = 'Please enter a valid email address';
      if (control?.value?.split('@')[1] === WG) {
        this.emailError = WG_MESSAGE;
        return { notwinning: true };
      }
      return null;
    };
  }

  signup() {
    const email = this.signupForm.value.signupEmail;
    const domain = email.split('@')[1];
    if (domain === WG) {
      this.emailError = WG_MESSAGE;
      this.signupForm.get('signupEmail')?.setErrors({ invalid: true });
    } else {
      this.passwordError = 'Passwords must be at least 8 characters in length';
      this.authService.register(this.signupForm.value.signupEmail).subscribe({
        next: (response) => {
          if (response.status === 'success') {
            this.authService.userId = response.user._id;
            this.totpSecret = response.user.totpSecret;
            this.email = this.signupForm.value.signupEmail;
            this.showForm = 'signupconfirm';
          }
        },
        error: (error) => {
          this.emailError = error.message;
          this.signupForm.get('signupEmail')?.setErrors({ invalid: true });
        }
      });
    }
  }

  signupConfirm() {
    this.authService.registerConfirm(
      this.authService.email,
      this.signupConfirmForm.value.signupFullName,
      this.signupConfirmForm.value.newPassword,
      this.signupConfirmForm.value.signupAuthCode)
      .subscribe({
        next: (response) => {
          if (response.status === 'success') {
            this.messages = [signUpPendingMessage];
            this.signupForm.reset();
            this.signupConfirmForm.reset();
            this.showForm = 'login';
          }
        },
        error: (error) => {
          this.signupConfirmForm.get('signupAuthCode')?.setErrors({ invalid: true });
        }
      });
  }

}
